import { isNullOrEmpty } from "../../core/helpers/string-util.js";
import AbstractAccountGroup from "./AbstractAccountGroup.js";

/**
 * `accountListGroup` represent a leaf in the CoA tree. It keeps
 * a list of account numbers/identifiers for accounts included in the group.
 *
 * @param {string[]} [spec.accounts] - Array of account numbers/identifiers
 * @param {string} [spec.commaSeparatedAccounts] - Comma separated string of account numbers/identifiers
 */
export default class AccountListGroup extends AbstractAccountGroup {
	constructor({ accounts = [], commaSeparatedAccounts } = {}) {
		super(...arguments);

		if (commaSeparatedAccounts) {
			accounts = commaSeparatedListToArray(commaSeparatedAccounts);
		}

		this._accounts = accounts;

		if (!this._name && this._accounts) {
			this._name = this.getAsCommaSeparatedString();
		}
	}

	getName() {
		return this.getAccountsString();
	}

	getAccountsString() {
		return this._accounts.join(", ");
	}

	getAccounts() {
		return this._accounts;
	}

	setAccounts(newAccounts) {
		this._accounts = newAccounts;
	}

	addAccounts(accountNumbers) {
		this._accounts.push(...accountNumbers);
	}

	removeAccount(accountNumber) {
		this._accounts = this._accounts.filter(
			(number) => number !== accountNumber,
		);
	}

	getAsCommaSeparatedString() {
		return this._accounts.join(", ");
	}

	setFromCommaSeparatedString(string) {
		this._accounts = commaSeparatedListToArray(string);
	}

	matchString(string) {
		return this._accounts.some((number) => string === number);
	}

	accept(visitor) {
		return visitor.visitAccountListGroup(this);
	}

	canBeAddedTo(group) {
		return group.canAddAccountList(this);
	}

	canBeRemoved() {
		return true;
	}

	canAdd() {
		return false;
	}

	isAccountList() {
		return true;
	}

	hasAccounts() {
		return this._accounts.length > 0;
	}

	hasAccount(account) {
		return this._accounts.some(
			(accountNumber) => accountNumber === account.getNumber(),
		);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.accounts = this._accounts;

		return properties;
	}

	static getType() {
		return "AccountListGroup";
	}
}

function commaSeparatedListToArray(string) {
	return string
		.split(",")
		.map((item) => item.trim())
		.filter((item) => !isNullOrEmpty(item));
}
